/**
 * Shopify Variant Service
 * 
 * This service handles all variant-related operations including creation,
 * updates, and inventory management for Shopify products.
 */

import { ShopifyGraphQLService } from './shopifyGraphQLService';

export class ShopifyVariantService {
  constructor(shop, accessToken) {
    this.graphqlService = new ShopifyGraphQLService(shop, accessToken);
  }

  /**
   * Creates variants in bulk for a product
   * @param {string} productId - The ID of the product
   * @param {Array} variants - Array of variant data
   * @returns {Promise<Array>} Created variants
   */
  async createVariants(productId, variants) {
    const createVariantsBulkMutation = `
      mutation productVariantsBulkCreate($productId: ID!, $variants: [ProductVariantsBulkInput!]!) {
        productVariantsBulkCreate(productId: $productId, variants: $variants) {
          productVariants {
            id
            sku
            title
            price
            compareAtPrice
            selectedOptions {
              name
              value
            }
            optionValues {
              optionName
              name
            }
            inventoryItem {
              id
              sku
            }
          }
          userErrors {
            field
            message
          }
        }
      }
    `;

    // Format variants according to Shopify API 2025-01 requirements
    const formattedVariants = variants.map(variant => {
      // Ensure we have the correct option value format
      const optionValues = variant.options.map((value, index) => ({
        optionName: variant.optionNames[index],
        name: value
      }));

      return {
        sku: variant.sku,
        price: variant.price ? variant.price.toString() : '0.00',
        compareAtPrice: variant.compareAtPrice ? variant.compareAtPrice.toString() : null,
        barcode: variant.barcode,
        taxable: variant.taxable !== false,
        inventoryPolicy: variant.inventoryPolicy || 'DENY',
        optionValues,
        inventoryItem: variant.sku ? {
          sku: variant.sku
        } : undefined,
        weight: variant.weight ? parseFloat(variant.weight) : undefined,
        weightUnit: variant.weightUnit || 'KILOGRAMS',
        requiresShipping: variant.requiresShipping !== false,
        harmonizedSystemCode: variant.harmonizedSystemCode,
        inventoryManagement: variant.inventoryManagement || "SHOPIFY"
      };
    });

    console.log(`Creating ${formattedVariants.length} variants for product ${productId}`);
    console.log('Formatted variants:', JSON.stringify(formattedVariants, null, 2));

    const result = await this.graphqlService.makeGraphQLRequest(createVariantsBulkMutation, {
      productId,
      variants: formattedVariants
    });

    if (result?.productVariantsBulkCreate?.userErrors?.length > 0) {
      const errors = result.productVariantsBulkCreate.userErrors;
      console.error('Errors creating variants:', errors);
      throw new Error(`Failed to create variants: ${JSON.stringify(errors)}`);
    }

    return result?.productVariantsBulkCreate?.productVariants || [];
  }

  /**
   * Updates variants in bulk
   * @param {Array} variants - Array of variant data with IDs
   * @returns {Promise<Array>} Updated variants
   */
  async updateVariants(variants) {
    const updateVariantsBulkMutation = `
      mutation productVariantsBulkUpdate($variants: [ProductVariantsBulkInput!]!) {
        productVariantsBulkUpdate(variants: $variants) {
          variants {
            id
            sku
            price
            compareAtPrice
            selectedOptions {
              name
              value
            }
            optionValues {
              optionName
              name
            }
            inventoryItem {
              id
              sku
            }
          }
          userErrors {
            field
            message
          }
        }
      }
    `;

    // Format variants according to Shopify API 2025-01 requirements
    const formattedVariants = variants.map(variant => {
      // Ensure we have the correct option value format
      const optionValues = variant.options.map((value, index) => ({
        optionName: variant.optionNames[index],
        name: value
      }));

      return {
        id: variant.id,
        price: variant.price ? variant.price.toString() : '0.00',
        compareAtPrice: variant.compareAtPrice ? variant.compareAtPrice.toString() : null,
        barcode: variant.barcode,
        taxable: variant.taxable !== false,
        inventoryPolicy: variant.inventoryPolicy || 'DENY',
        optionValues,
        inventoryItem: variant.sku ? {
          sku: variant.sku
        } : undefined,
        weight: variant.weight ? parseFloat(variant.weight) : undefined,
        weightUnit: variant.weightUnit || 'KILOGRAMS',
        requiresShipping: variant.requiresShipping !== false
      };
    });

    console.log(`Updating ${formattedVariants.length} variants`);
    console.log('Formatted variants:', JSON.stringify(formattedVariants, null, 2));

    const result = await this.graphqlService.makeGraphQLRequest(updateVariantsBulkMutation, {
      variants: formattedVariants
    });

    if (result?.productVariantsBulkUpdate?.userErrors?.length > 0) {
      const errors = result.productVariantsBulkUpdate.userErrors;
      console.error('Errors updating variants:', errors);
      throw new Error(`Failed to update variants: ${JSON.stringify(errors)}`);
    }

    return result?.productVariantsBulkUpdate?.variants || [];
  }

  /**
   * Updates a variant's SKU
   * @param {string} inventoryItemId - The ID of the inventory item
   * @param {string} sku - New SKU value
   * @returns {Promise<object>} Updated variant data
   */
  async updateVariantSku(inventoryItemId, sku) {
    const updateInventoryMutation = `
      mutation inventoryItemUpdate($id: ID!, $input: InventoryItemUpdateInput!) {
        inventoryItemUpdate(id: $id, input: $input) {
          inventoryItem {
            id
            sku
          }
          userErrors {
            field
            message
          }
        }
      }
    `;

    const result = await this.graphqlService.mutate(updateInventoryMutation, {
      id: inventoryItemId,
      input: {
        sku: sku
      }
    });

    if (result?.inventoryItemUpdate?.userErrors?.length > 0) {
      const errors = result.inventoryItemUpdate.userErrors;
      throw new Error(`Failed to update variant SKU: ${JSON.stringify(errors)}`);
    }

    return result?.inventoryItemUpdate?.inventoryItem;
  }

  /**
   * Fetches variant details by ID
   * @param {string} variantId - The ID of the variant
   * @returns {Promise<object>} Variant details
   */
  async getVariantDetails(variantId) {
    const variantDetailsQuery = `
      query getVariant($id: ID!) {
        productVariant(id: $id) {
          id
          sku
          inventoryItem {
            id
            sku
          }
        }
      }
    `;

    const result = await this.graphqlService.query(variantDetailsQuery, {
      id: variantId
    });

    return result?.productVariant;
  }

  /**
   * Updates inventory tracking for a variant
   * @param {string} inventoryItemId - The ID of the inventory item
   * @param {boolean} trackingEnabled - Whether to enable inventory tracking
   * @returns {Promise<object>} Updated inventory item data
   */
  async updateInventoryTracking(inventoryItemId, trackingEnabled) {
    const updateInventoryTrackingMutation = `
      mutation inventoryItemUpdate($id: ID!, $input: InventoryItemUpdateInput!) {
        inventoryItemUpdate(id: $id, input: $input) {
          inventoryItem {
            id
            tracked
          }
          userErrors {
            field
            message
          }
        }
      }
    `;

    const variables = {
      id: inventoryItemId,
      input: {
        tracked: trackingEnabled
      }
    };

    const result = await this.graphqlService.mutate(updateInventoryTrackingMutation, variables);

    if (result?.inventoryItemUpdate?.userErrors?.length > 0) {
      const errors = result.inventoryItemUpdate.userErrors;
      throw new Error(`Failed to update inventory tracking: ${JSON.stringify(errors)}`);
    }

    return result?.inventoryItemUpdate?.inventoryItem;
  }

  /**
   * Updates inventory quantity for a variant at a specific location
   * @param {string} inventoryItemId - The ID of the inventory item
   * @param {string} locationId - The ID of the location
   * @param {number} quantity - The new quantity
   * @returns {Promise<{success: boolean, error?: string}>} Result of the inventory update
   */
  async updateInventoryQuantity(inventoryItemId, locationId, quantity) {
    try {
      // Validate inputs
      if (!inventoryItemId) {
        console.error('Missing inventoryItemId for inventory update');
        return { success: false, error: 'Missing inventory item ID' };
      }

      if (!locationId) {
        console.error('Missing locationId for inventory update');
        return { success: false, error: 'Missing location ID' };
      }

      // Ensure quantity is a valid number
      const parsedQuantity = parseInt(quantity, 10);
      if (isNaN(parsedQuantity) || parsedQuantity < 0) {
        console.error(`Invalid quantity for inventory update: ${quantity}`);
        return { success: false, error: 'Invalid quantity for inventory update' };
      }

      const updateInventoryMutation = `
        mutation inventorySetOnHandQuantities($input: InventorySetOnHandQuantitiesInput!) {
          inventorySetOnHandQuantities(input: $input) {
            inventoryLevels {
              id
              available
              item {
                id
                sku
              }
              location {
                id
                name
              }
            }
            userErrors {
              field
              message
            }
          }
        }
      `;

      const variables = {
        input: {
          reason: "correction",
          setQuantities: [{
            inventoryItemId,
            locationId,
            quantity: parsedQuantity
          }]
        }
      };

      console.log(`Updating inventory quantity for item ${inventoryItemId} at location ${locationId} to ${parsedQuantity}`);
      
      const result = await this.graphqlService.makeGraphQLRequest(updateInventoryMutation, variables);

      if (result?.inventorySetOnHandQuantities?.userErrors?.length > 0) {
        const errors = result.inventorySetOnHandQuantities.userErrors;
        console.error('Failed to update inventory quantity:', errors);
        return {
          success: false,
          error: errors.map(e => e.message).join(', ')
        };
      }

      const updatedLevel = result?.inventorySetOnHandQuantities?.inventoryLevels?.[0];
      if (!updatedLevel) {
        console.error('No inventory level returned from update');
        return {
          success: false,
          error: 'No inventory level returned from update'
        };
      }

      console.log(`Successfully updated inventory quantity to ${updatedLevel.available}`);
      return {
        success: true,
        inventoryLevel: updatedLevel
      };
    } catch (error) {
      console.error('Error updating inventory quantity:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Gets the default location for inventory
   * @returns {Promise<string>} The ID of the default location
   */
  async getDefaultLocation() {
    try {
      const query = `
        query {
          locations(first: 1, sortKey: CREATED_AT) {
            edges {
              node {
                id
                name
                isActive
              }
            }
          }
        }
      `;

      const result = await this.graphqlService.makeGraphQLRequest(query);
      const location = result?.locations?.edges?.[0]?.node;

      if (!location) {
        throw new Error('No active locations found');
      }

      console.log(`Using default location: ${location.name} (${location.id})`);
      return location.id;
    } catch (error) {
      console.error('Error getting default location:', error);
      throw error;
    }
  }
}
